<template>
    <div class="no-print" id="header-component">
        <!-- background-container -->
        <div class="fixed inset-0 z-0 -top-20 overflow-hidden" id="imageBackground">
            <!-- Overlay for fade-out effect as you scroll -->
            <div class="absolute inset-0 transition-opacity duration-1000 ease-in-out" :style="overlayStyle"></div>
            <!-- Mobile (<768px): Single JCP image, no slider -->
            <div v-if="isMobile" class="absolute inset-0">
                <picture class="w-full h-full block">
                    <source media="(max-width: 320px)" :srcset="jcpImage.mobile_sm" />
                    <source media="(max-width: 375px)" :srcset="jcpImage.mobile_lg" />
                    <source media="(max-width: 768px)" :srcset="jcpImage.tablet_sm" />
                    <img
                        class="w-full h-full object-cover block"
                        :src="jcpImage.normal"
                        alt="Hero background"
                        loading="eager"
                    />
                </picture>
            </div>

            <!-- Larger screens (>=768px): Slider -->
            <div v-else-if="hasMounted">
                <div
                    v-for="(image, index) in images"
                    :key="index"
                    class="absolute inset-0 transition-opacity duration-1000 ease-in-out"
                    :class="{ 'opacity-0': currentIndex !== index }"
                >
                    <picture class="w-full h-full block">
                        <source media="(max-width: 768px)" :srcset="image.tablet_sm" />
                        <source media="(max-width: 1024px)" :srcset="image.tablet_lg" />
                        <source media="(max-width: 1280px)" :srcset="image.laptop_sm" />
                        <source media="(max-width: 1440px)" :srcset="image.laptop_lg" />
                        <img
                            class="w-full h-full object-cover block"
                            :src="image.normal"
                            alt="Hero slide background"
                            :loading="index === 0 ? 'eager' : 'lazy'"
                        />
                    </picture>
                </div>
            </div>
        </div>

        <!-- Rest of your header contents (logo, nav, etc.) remain unchanged -->
        <div class="relative -left-2 tablet-sm:-left-0 w-full">
            <div
                class="max-w-screen-md laptop-sm:max-w-screen-lg desktop:max-w-screen-xl ultra-wide:max-w-screen-2xl h-20 tablet-sm:h-32 w-full relative block px-10 pt-2 pb-3 mt-8 mx-auto overflow-hidden opacity-100 text-black text-base leading-155 font-sans"
            >
                <div
                    class="block absolute inset-y-0 right-0 left-12 m-0 p-0 bg-secondary-bg skew-x-[33deg] text-black text-base leading-155 font-sans"
                    style="
                        border-width: 2px;
                        border-style: solid;
                        border-image: linear-gradient(to right, rgba(255, 255, 255, 1) 25%, rgba(255, 255, 255, 0) 100%) 1;
                    "
                >
                    <div
                        class="h-80px w-30px tablet-sm:h-150px tablet-sm:w-52px -right-2 laptop-sm:right-0 absolute block bg-secondary-bg -top-2 skew-x-[-33deg] text-black text-base leading-155 font-sans"
                    >
                         
                    </div>
                </div>

                <!-- Logo -->
                <div class="flex pl-8 justify-between items-center z-50 h-full">
                    <div>
                        <Link
                            :href="'/'"
                            class="py-3 pb-4 px-6 nav-link justify-center items-center rising-underline"
                            preserve-state
                            preserve-scroll
                        >
                            <img
                                id="remax-logo"
                                src="/images/theme/logo.png"
                                alt="MortgaaS"
                                class="w-36 tablet-sm:w-56 laptop-sm:w-64 desktop:w-72 relative z-40"
                            />
                        </Link>
                    </div>
                </div>
            </div>

            <div
                class="max-w-screen-md laptop-sm:max-w-screen-lg desktop:max-w-screen-xl ultra-wide:max-w-screen-2xl relative w-full flex justify-end mx-auto"
            >
                <div class="w-full tablet-sm:w-auto absolute bottom-0 right-8">
                    <PrimaryNav />
                </div>
            </div>
        </div>

        <!-- Content area -->
        <div
            class="relative w-full overflow-hidden z-0 items-center"
            :class="[
                is404 ? 'h-12' :
                isHomePage ? 'h-44 tablet-sm:h-48' :
                'h-24 tablet-sm:h-60 laptop-sm:h-72 transition-all duration-1000 ease-in-out'
            ]"
        >
            <div
                class="tablet-sm:max-w-xl laptop-sm:max-w-3xl desktop:max-w-5xl ultra-wide:max-w-6xl w-full fixed left-1/2 transform -translate-x-1/2 translate-y-18 transition-opacity duration-1000 ease-in-out height-prop"
            >
                <transition
                    mode="out-in"
                    enter-active-class="transition-all duration-1000 ease-out"
                    leave-active-class="transition-all duration-500 ease-in"
                    enter-from-class="opacity-0 transform translate-y-4"
                    enter-to-class="opacity-100 transform translate-y-0"
                    leave-from-class="opacity-100 transform translate-y-0"
                    leave-to-class="opacity-0 transform translate-y-4"
                >
                    <div key="home" v-if="isHomePage">
                        <h2
                            class="ml-0 tablet-sm:ml-auto px-2 tablet-sm:px-4 desktop:px-8
                                text-xs mobile-lg:text-sm tablet-sm:text-lg laptop-sm:text-2xl desktop:text-4xl
                                w-full text-white text-center font-title font-semibold
                                bg-primary-text bg-opacity-75 tablet-sm:bg-opacity-50"
                            :style="parallax2"
                        >
                            Real Estate is a Breeze
                            <br />
                            When you Buy and Sell with Gina Betbeze
                        </h2>
                    </div>

                    <div
                        class="flex items-center justify-center"
                        :class="isHomePage ? 'pt-16 ' : 'pt-0 tablet-sm:pt-16'"
                        v-else
                        :key="pageTitle || 'placeholder'"
                    >
                        <h2
                            v-if="pageTitle"
                            class="text-3xl tablet-sm:text-5xl laptop-sm:text-6xl ml-0 tablet-sm:px-4 tablet-sm:px-12
                                text-white text-center font-title font-semibold z-30 bg-primary-text mr-0 py-4
                                bg-opacity-75 tablet-sm:bg-opacity-50 rounded-sm border-accent ring-2 ring-inset ring-accent
                                shadow-fading-box"
                            :style="parallax3"
                        >
                            {{ pageTitle }}
                        </h2>
                    </div>
                </transition>
            </div>

            <div
                v-if="isHomePage"
                class="
                    border-x-4 tablet-sm:border-x-auto border-y-0 tablet-sm:border-y-4 mt-20 mobile-xl:mt-36 tablet-sm:mt-48 from-25%
                    tablet-sm:from-0% to-white to-75
                    max-w-76 mobile-lg:max-w-xs mobile-xl:max-w-lg tablet-sm:max-w-2xl tablet-lg:max-w-4xl laptop-lg:max-w-6xl desktop:max-w-7xl
                    relative overflow-hidden bg-primary-bg mx-auto mb-12 rounded-sm
                    border border-y-4 border-accent w-full justify-center flex bg-gradient-to-r from-accent
                "
            >
                <ProfileBlurb
                    :class="
                        isHomePage
                            ? 'opacity-100 transition-opacity duration-1000'
                            : 'opacity-0 transition-opacity duration-1000'
                    "
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import PrimaryNav from "../../Components/Layout/PrimaryNav.vue";
import ProfileBlurb from "../PageContent/ProfileBlurb.vue";
import { usePage } from "@inertiajs/vue3";
import { Link } from "@inertiajs/vue3";

// ─────────────────────────────────────────────────────────────────────────────
// PROPS
const props = defineProps({
    images: {
        type: Array,
        default: () => [
            {
                mobile_sm: "/images/theme/hero_slider/Rivertown-320.webp",
                mobile_lg: "/images/theme/hero_slider/Rivertown-375.webp",
                tablet_sm: "/images/theme/hero_slider/Rivertown-768.webp",
                tablet_lg: "/images/theme/hero_slider/Rivertown-1024.webp",
                laptop_sm: "/images/theme/hero_slider/Rivertown-1280.webp",
                laptop_lg: "/images/theme/hero_slider/Rivertown-1440.webp",
                normal: "/images/theme/hero_slider/Rivertown-1920.webp",
            },
            {
                mobile_sm: "/images/theme/hero_slider/woods-320.webp",
                mobile_lg: "/images/theme/hero_slider/woods-375.webp",
                tablet_sm: "/images/theme/hero_slider/woods-768.webp",
                tablet_lg: "/images/theme/hero_slider/woods-1024.webp",
                laptop_sm: "/images/theme/hero_slider/woods-1280.webp",
                laptop_lg: "/images/theme/hero_slider/woods-1440.webp",
                normal: "/images/theme/hero_slider/woods-1920.webp",
            },
            {
                mobile_sm: "/images/theme/hero_slider/sanmarco-320.webp",
                mobile_lg: "/images/theme/hero_slider/sanmarco-375.webp",
                tablet_sm: "/images/theme/hero_slider/sanmarco-768.webp",
                tablet_lg: "/images/theme/hero_slider/sanmarco-1024.webp",
                laptop_sm: "/images/theme/hero_slider/sanmarco-1280.webp",
                laptop_lg: "/images/theme/hero_slider/sanmarco-1440.webp",
                normal: "/images/theme/hero_slider/sanmarco-1920.webp",
            },
            {
                mobile_sm: "/images/theme/hero_slider/pool-320.webp",
                mobile_lg: "/images/theme/hero_slider/pool-375.webp",
                tablet_sm: "/images/theme/hero_slider/pool-768.webp",
                tablet_lg: "/images/theme/hero_slider/pool-1024.webp",
                laptop_sm: "/images/theme/hero_slider/pool-1280.webp",
                laptop_lg: "/images/theme/hero_slider/pool-1440.webp",
                normal: "/images/theme/hero_slider/pool-1920.webp",
            },
            {
                mobile_sm: "/images/theme/hero_slider/mandarin-320.webp",
                mobile_lg: "/images/theme/hero_slider/mandarin-375.webp",
                tablet_sm: "/images/theme/hero_slider/mandarin-768.webp",
                tablet_lg: "/images/theme/hero_slider/mandarin-1024.webp",
                laptop_sm: "/images/theme/hero_slider/mandarin-1280.webp",
                laptop_lg: "/images/theme/hero_slider/mandarin-1440.webp",
                normal: "/images/theme/hero_slider/mandarin-1920.webp",
            },
            {
                mobile_sm: "/images/theme/hero_slider/flag-320.webp",
                mobile_lg: "/images/theme/hero_slider/flag-375.webp",
                tablet_sm: "/images/theme/hero_slider/flag-768.webp",
                tablet_lg: "/images/theme/hero_slider/flag-1024.webp",
                laptop_sm: "/images/theme/hero_slider/flag-1280.webp",
                laptop_lg: "/images/theme/hero_slider/flag-1440.webp",
                normal: "/images/theme/hero_slider/flag-1920.webp",
            },
            {
                mobile_sm: "/images/theme/hero_slider/jcp-320.webp",
                mobile_lg: "/images/theme/hero_slider/jcp-375.webp",
                tablet_sm: "/images/theme/hero_slider/jcp-768.webp",
                tablet_lg: "/images/theme/hero_slider/jcp-1024.webp",
                laptop_sm: "/images/theme/hero_slider/jcp-1280.webp",
                laptop_lg: "/images/theme/hero_slider/jcp-1440.webp",
                normal: "/images/theme/hero_slider/jcp-1920.webp",
            },
        ],
    },
    interval: {
        type: Number,
        default: 9000,
    },
    title: {
        type: String,
        default: "Gina Betbeze - Real Estate",
    },
});

// ─────────────────────────────────────────────────────────────────────────────
// STATE & COMPUTED
const currentIndex = ref(0);
const scrollY = ref(0);
const parallax2 = ref({});
const parallax3 = ref({});
const isMobile = ref(false);
const hasMounted = ref(false);

// Extract JCP image (last item in the array)
const jcpImage = computed(() => props.images[props.images.length - 1]);

// Inertia “page” and pageTitle logic
const page = usePage();
const isHomePage = computed(() => {
    const url = new URL(page.props.ziggy.location);
    return url.pathname === '/';
});
const is404 = ref(false);
const pageTitle = computed(() => page.props.title || props.title || "Gina Betbeze");

// Overlay fade-out as you scroll
const overlayStyle = computed(() => {
    const maxScroll = 700;
    const opacity = Math.min(scrollY.value / maxScroll, 0.8);
    return {
        backgroundColor: `rgba(249, 249, 249, ${opacity})`,
        opacity: scrollY.value > 700 ? 1 : opacity,
    };
});

// Parallax transforms for the H2 text blocks
const lerp = (a, b, t) => a * (1 - t) + b * t;
let lastScrollY = 0;
const handleScroll = () => {
    const targetScrollY = window.scrollY;
    lastScrollY = lerp(lastScrollY, targetScrollY, 0.1);
    scrollY.value = targetScrollY;
    parallax2.value = {
        transform: `translateY(${Math.round(targetScrollY * 0.15)}px)`,
    };
    parallax3.value = {
        transform: `translateY(${Math.round(targetScrollY * -0.6)}px)`,
    };
};

// Rotate slider index every “interval” milliseconds (non-mobile only)
let intervalId;
const nextImage = () => {
    currentIndex.value = (currentIndex.value + 1) % props.images.length;
};

// Check if device is mobile (<768px)
const checkMobile = () => {
    isMobile.value = window.innerWidth < 768;
};

// ─────────────────────────────────────────────────────────────────────────────
onMounted(() => {
    hasMounted.value = true
    const page = usePage();
    const url = page.url;

    is404.value = url === "/404";
    // Check mobile status on mount
    checkMobile();
    // Listen for resize to update mobile status
    window.addEventListener("resize", checkMobile, { passive: true });
    // Listen for scroll → parallax
    window.addEventListener("scroll", handleScroll, { passive: true });
    // Rotate through images (non-mobile only)
    if (!isMobile.value) {
        intervalId = setInterval(nextImage, props.interval);
    }
    // Kickstart parallax immediately
    handleScroll();
});

onUnmounted(() => {
    window.removeEventListener("resize", checkMobile);
    window.removeEventListener("scroll", handleScroll);
    clearInterval(intervalId);
});

watch(isMobile, (newVal) => {
    if (!newVal && !intervalId) {
        intervalId = setInterval(nextImage, props.interval);
    } else if (newVal && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
    }
});
</script>

<style scoped>
/* Utility classes (unchanged) */
.transition-opacity {
    transition-property: opacity;
}
.transition-all {
    transition-property: all;
}
.duration-500 {
    transition-duration: 500ms;
}
.shadow-fading-box {
    box-shadow:
        0 0 10px rgba(0, 0, 0, 0.1),
        0 0 20px rgba(0, 0, 0, 0.1),
        0 0 30px rgba(0, 0, 0, 0.1),
        0 0 40px rgba(0, 0, 0, 0.1),
        0 0 50px rgba(0, 0, 0, 0.1),
        0 0 60px rgba(0, 0, 0, 0.1),
        0 0 70px rgba(0, 0, 0, 0.1);
}
h2 {
    will-change: transform;
}
</style>
